import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  queryIsLoading,
  useGetFootprintingCropPrimaryDataById,
  useGetFootprintingCropSeedingsByPrimaryDataId,
  useGetPracticeSeedings,
  useKeyedFormList,
  useMutateFootprintingCropPrimaryData,
  useMutateFootprintingCropSeedings,
  useTranslation,
} from "utils";

import { PRACTICE_SLUG, ZERO_UUID } from "models";

import {
  Button,
  Card,
  Col,
  Flex,
  Form,
  FormInstance,
  Icon,
  IconButton,
  InputNumber,
  KlimRadioGroup,
  Option,
  PageSpin,
  Row,
  Select,
  Tooltip,
  Typography,
} from "components";

interface CropSeedingFormProps {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  cropId: string;
  year: number;
}

interface AppliedPractice {
  id?: string;
  practiceId: string;
  implementedOnHa: number | null;
}

const blankPractice: AppliedPractice = {
  id: undefined,
  practiceId: "",
  implementedOnHa: null,
};

const CropSeedingForm = ({ primaryDataId, readOnly, farmId, cropId, year }: CropSeedingFormProps) => {
  const { t } = useTranslation(["footprintingCrop", "practices"]);
  const formRef = useRef<FormInstance>(null);

  const cropPrimaryDataQuery = useGetFootprintingCropPrimaryDataById(primaryDataId ?? "");

  // Form elements
  const [seedingActive, setSeedingActive] = useState<boolean | null>(null);

  // GET Queries
  const seedingPractices = useGetPracticeSeedings({ enabled: true });
  const seedingsQuery = useGetFootprintingCropSeedingsByPrimaryDataId(primaryDataId);
  const isLoading = [cropPrimaryDataQuery, seedingPractices, seedingsQuery].some(queryIsLoading);
  const keyedSeedings = useKeyedFormList<AppliedPractice>(formRef, { isReady: !isLoading });

  // Mutations
  const { mutate: mutateSeedings } = useMutateFootprintingCropSeedings(
    farmId,
    cropId,
    year,
    keyedSeedings.entries
      .filter((t) => !!t.practiceId)
      .map((s) => ({
        id: s.id,
        footprintingFarmCropPrimaryDataId: primaryDataId ?? "",
        practicesSeedingId: s.practiceId,
        implementedOnHa: s.implementedOnHa ?? null,
      })),
    primaryDataId ?? ""
  );

  const { mutate: mutateCropPrimaryData } = useMutateFootprintingCropPrimaryData(
    farmId,
    cropId,
    year,
    {
      farmId,
      cropId,
      year,
      seedingActive,
    },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateSeedings();
        mutateCropPrimaryData();
      }
    },
    [isLoading, mutateCropPrimaryData, mutateSeedings, readOnly]
  );

  // Note - this will only run once because the query above is only the GET
  useEffect(() => {
    if (seedingsQuery.data) {
      keyedSeedings.actions.hydrate(
        seedingsQuery.data.length
          ? seedingsQuery.data.map((s) => ({
              id: s.id,
              practiceId: s.practicesSeedingId,
              implementedOnHa: s.implementedOnHa,
            }))
          : [blankPractice]
      );
    }
  }, [seedingsQuery.data, keyedSeedings.actions]);

  useEffect(() => {
    if (cropPrimaryDataQuery.data && cropPrimaryDataQuery.isSuccess) {
      setSeedingActive(cropPrimaryDataQuery.data.seedingActive ?? null);

      formRef.current?.setFieldsValue({
        seedingActive: cropPrimaryDataQuery.data.seedingActive ?? null,
      });

      // this stops the inputs from validating on first form load, but will validate if user
      // nagivates forward and then back with an input that was left as null
      const fieldsToValidate = (["seedingActive"] as const).some((field) => cropPrimaryDataQuery.data?.[field] !== null);

      if (fieldsToValidate) {
        keyedSeedings.actions.requestRehydrationValidation();
      }
    }
  }, [cropPrimaryDataQuery.data, cropPrimaryDataQuery.isSuccess, keyedSeedings.actions]);

  if (primaryDataId && isLoading) return <PageSpin />;

  const seedingsSelectGroupName = "footprinting-crop-seedings-select";

  const disabledSeedings: Record<string, boolean> = keyedSeedings.entries.reduce((acc, t) => ({ ...acc, [t.practiceId]: true }), {} as Record<string, boolean>);

  return (
    <Form dataTestId="footprinting-crop-seeding-form" ref={formRef}>
      <Card border className="padding-100">
        <Flex align="center" gap={16} justify="space-between" className="padding-100">
          <Typography variant="paragraph-m">
            {t("cropSeedingForm.seeding.question")}
            {true && <span className="required-asterisk">*</span>}
            <Tooltip title={t("cropSeedingForm.tooltips.questionSeeding")}>
              <Icon size="s" iconName="tabler/info-circle" className="input-label-info-icon" />
            </Tooltip>
          </Typography>
          <KlimRadioGroup
            disabled={readOnly}
            name="seedingActive"
            dataTestId="footprinting-crop-seeding-active-radio"
            required
            vertical={false}
            type="tertiary"
            size="large"
            onChange={({ target }) => {
              setSeedingActive(target.value);
            }}
            value={seedingActive}
            defaultValue={seedingActive}
            optionType="button"
            options={[true, false].map((opt) => ({
              label: t(`footprinting:radioAnswer.${opt}`),
              value: opt,
            }))}
          />
        </Flex>
        {seedingActive &&
          keyedSeedings.entries.map((seeding, index) => {
            const groupWithIndex = `${seedingsSelectGroupName}-${index}`;
            const groupWithKey = `${seedingsSelectGroupName}-${seeding.clientKey}`;

            return (
              <Form.Item key={seeding.clientKey} className="padding-100">
                <Flex vertical gap={16}>
                  <Flex justify="space-between" vertical={false}>
                    <Typography variant="h5" style={{ flex: 1 }}>
                      {t("cropSeedingForm.seeding.count") + (index + 1)}
                    </Typography>
                    <IconButton
                      disabled={readOnly}
                      type="tertiary"
                      size="small"
                      dataTestId={`${groupWithIndex}-delete`}
                      iconName="tabler/trash"
                      onClick={() => {
                        keyedSeedings.actions.remove(seeding.clientKey);
                        if (keyedSeedings.entries.length === 1) setSeedingActive(null);
                      }}
                    />
                  </Flex>
                  <Row>
                    <Col xs={28} sm={14}>
                      <Select
                        name={groupWithKey}
                        disabled={readOnly}
                        dataTestId={groupWithIndex}
                        label={t("cropSeedingForm.seeding.type")}
                        tooltip={t("cropSeedingForm.tooltips.seedingType")}
                        showSearch
                        rules={[{ required: true, message: "" }]}
                        initialValue={seeding.practiceId}
                        value={seeding.practiceId}
                        onChange={(v) => keyedSeedings.actions.update(seeding.clientKey, { practiceId: v })}
                        dropdownStyle={{ zIndex: 10010 }} // The modal presenting us has a z-index of 10k
                      >
                        {(seedingPractices.data ?? [])
                          .filter((practice) => practice.id !== ZERO_UUID)
                          .map((seeding) => (
                            <Option
                              disabled={readOnly || disabledSeedings[seeding.id]}
                              key={`${groupWithIndex}-${seeding.id}`}
                              data-testid={`${groupWithIndex}-${seeding.id}`}
                              value={seeding.id}
                            >
                              {t(`${PRACTICE_SLUG.SEEDINGS}.${seeding.translationSlug}`, { ns: "practices" })}
                            </Option>
                          ))}
                      </Select>
                    </Col>

                    <Col xs={20} sm={10} className="padding-0 padding-left-50">
                      <InputNumber
                        name={`${groupWithKey}-coverage`}
                        dataTestId={`${groupWithIndex}-coverage`}
                        disabled={readOnly}
                        label={t("cropSeedingForm.question.implementedOn")}
                        tooltip={t("cropSeedingForm.tooltips.implementSeedingOn")}
                        suffix="ha"
                        value={seeding.implementedOnHa}
                        initialValue={seeding.implementedOnHa}
                        onChange={(v) => keyedSeedings.actions.update(seeding.clientKey, { implementedOnHa: v })}
                        validateFirst
                        precision={2}
                        rules={[
                          { required: true, type: "number", message: t("cropSeedingForm.error.validNumber") },
                          {
                            validator: (_: unknown, v: number) => (isNullOrUndefined(v) || isValidNonnegativeNumber(v) ? Promise.resolve() : Promise.reject()),
                            message: t("cropSeedingForm.error.validNumber"),
                          },
                        ]}
                      />
                    </Col>
                  </Row>
                </Flex>
              </Form.Item>
            );
          })}
        {seedingActive && (
          <Button
            disabled={readOnly}
            type="text"
            onClick={() => {
              keyedSeedings.actions.add(blankPractice);
            }}
          >
            <Icon iconName="klim/plus" className="icon inline" /> {t("cropSeedingForm.seeding.add")}
          </Button>
        )}
      </Card>
    </Form>
  );
};

CropSeedingForm.Key = "crop.seeding";
export { CropSeedingForm };
